// AI-powered executive resume review using the connected OpenAI integration.
//
// The OpenAI API key connected via the Magic Patterns Integrations panel is
// exposed to the client as an environment variable. We read it defensively so
// the feature degrades gracefully if it is unavailable.

export interface ResumeReview {
  overallScore: number // 0-100
  summary: string
  strengths: string[]
  gaps: string[]
  governancePositioning: string
  rewriteSuggestions: { before: string; after: string }[]
  recommendedHeadline: string
}

function getApiKey(): string | undefined {
  // Magic Patterns injects the connected key as a Vite env var.
  const env = (import.meta as any)?.env ?? {}
  return (
    env.VITE_OPENAI_API_KEY ||
    env.OPENAI_API_KEY ||
    env.VITE_OPENAI_KEY ||
    undefined
  )
}

export class MissingKeyError extends Error {
  constructor() {
    super(
      'OpenAI is not connected. Connect your OpenAI API key in the Integrations panel to enable AI resume reviews.',
    )
    this.name = 'MissingKeyError'
  }
}

const SYSTEM_PROMPT = `You are a world-class executive and board resume strategist for a premium board advisory firm.
You review resumes for senior leaders targeting Board of Directors, Advisory Board, Chairman, and C-Suite roles.
Evaluate the resume specifically through the lens of corporate governance, strategic oversight, risk, board readiness, and executive brand positioning — NOT entry-level or operational hiring.
Be candid, specific, and constructive. Always respond with valid JSON only, matching the requested schema exactly.`

function buildUserPrompt(resumeText: string, targetGoal: string): string {
  return `TARGET GOAL: ${targetGoal}

RESUME / CAREER SUMMARY TO REVIEW:
"""
${resumeText}
"""

Return a JSON object with EXACTLY these fields:
{
  "overallScore": number (0-100, board/executive readiness),
  "summary": string (2-3 sentence executive-level assessment),
  "strengths": string[] (3-5 concise points framed for board/executive positioning),
  "gaps": string[] (3-5 concise, specific improvement areas relevant to the target goal),
  "governancePositioning": string (1 paragraph on how to better position for governance/board readiness),
  "rewriteSuggestions": [{ "before": string, "after": string }] (2-3 examples rewriting a weak line into a high-impact, board-ready line),
  "recommendedHeadline": string (a single polished executive headline / personal brand statement)
}`
}

export async function reviewResume(
  resumeText: string,
  targetGoal: string,
): Promise<ResumeReview> {
  const apiKey = getApiKey()
  if (!apiKey) {
    throw new MissingKeyError()
  }

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: 'gpt-4o-mini',
      temperature: 0.4,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: buildUserPrompt(resumeText, targetGoal) },
      ],
    }),
  })

  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    throw new Error(
      `The review service returned an error (${response.status}). ${detail.slice(0, 200)}`,
    )
  }

  const data = await response.json()
  const content = data?.choices?.[0]?.message?.content
  if (!content) {
    throw new Error('No review was returned. Please try again.')
  }

  let parsed: ResumeReview
  try {
    parsed = JSON.parse(content)
  } catch {
    throw new Error('The review could not be parsed. Please try again.')
  }

  // Normalize defensively so the UI never crashes on a malformed field.
  return {
    overallScore:
      typeof parsed.overallScore === 'number' ? parsed.overallScore : 0,
    summary: parsed.summary ?? '',
    strengths: Array.isArray(parsed.strengths) ? parsed.strengths : [],
    gaps: Array.isArray(parsed.gaps) ? parsed.gaps : [],
    governancePositioning: parsed.governancePositioning ?? '',
    rewriteSuggestions: Array.isArray(parsed.rewriteSuggestions)
      ? parsed.rewriteSuggestions
      : [],
    recommendedHeadline: parsed.recommendedHeadline ?? '',
  }
}
